/**
 * MGC-S360 · Store híbrido (backend Supabase + fallback localStorage)
 *
 * Estratégia:
 *   • Se o usuário estiver AUTENTICADO (Supabase JWT), tudo é persistido no
 *     backend (`sprints.projetos` + `mgc.itens.projeto_id`) com RLS canônica.
 *   • Se ANÔNIMO (sem sessão), cai no comportamento antigo (localStorage +
 *     seed determinístico). Isso preserva o modo de demonstração/preview.
 *
 * A API pública é a MESMA da versão anterior — a página de MGC-S360 não
 * precisa ser refatorada:
 *   • useS360MgcItens()  → lista completa (todos os projetos)
 *   • moveS360Item(id, targetArea, extra?) → move item + revalida
 *   • PROJETOS_MOCK      → 4 projetos-exemplo (usado pelo <select> do topo);
 *                          quando online, esses IDs são substituídos pelos
 *                          UUIDs reais do banco após bootstrap.
 *   • REGRAS_S360, REGRAS_COM_CIENTE, getProjetoDoItem, resetS360Seed
 */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { getAccessToken, getSupabase } from "@/lib/supabase";
import { impHeaders, scopeWorkspaceId } from "@/lib/impersonation";
import { markProjetoDirty, markProjetoMotorRan } from "@/lib/s360MotorState";

const LS_KEY_BASE = "s360_mgc_itens_v2";
const LS_PROJETOS_KEY_BASE = "s360_projetos_v1";
const EVENT = "s360-mgc:updated";
const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Phase 12.4 — Buckets segregados por workspace. Antes desse fix os itens do
// MGC e a lista de projetos ficavam num LS_KEY global, o que fazia o MGC de
// um cliente reaparecer no dashboard de outro cliente no mesmo browser.
function currentWorkspaceScope() {
  // Jun/2026 — usa o mesmo escopo do resto do app (ghost / contexto V360 do
  // Super Admin têm precedência sobre o `app_session`).
  return scopeWorkspaceId() || "mock";
}
function LS_KEY() { return `${LS_KEY_BASE}__${currentWorkspaceScope()}`; }
function LS_PROJETOS_KEY() { return `${LS_PROJETOS_KEY_BASE}__${currentWorkspaceScope()}`; }

// ─────────────────────────────────────────────────────────────────────────────
// Regras catalogadas — usadas por cards e filtros
// ─────────────────────────────────────────────────────────────────────────────
export const REGRAS_S360 = {
  r1:  { titulo: "Atividade órfã",              descricao: "Atividade criada sem responsável designado.",              tipo_default: "aviso",   sev_default: "media",   acao: "Incluir responsável" },
  r2:  { titulo: "Impacto no caminho crítico",  descricao: "Alteração de prazo/dependência move o caminho crítico.",    tipo_default: "decisao", sev_default: "alta",    acao: "Rever plano" },
  r3:  { titulo: "Sobrealocação de recurso",    descricao: "Pessoa alocada acima da capacidade semanal em N projetos.", tipo_default: "alerta",  sev_default: "alta",    acao: "Renegociar carga" },
  r4:  { titulo: "Bloqueio sem atualização",    descricao: "Atividade marcada como bloqueada há N dias sem update.",    tipo_default: "alerta",  sev_default: "alta",    acao: "Solicitar update" },
  r5:  { titulo: "Ritual desaparecido",         descricao: "Sprint chegou ao ritual sem que ele fosse realizado.",      tipo_default: "alerta",  sev_default: "alta",    acao: "Reagendar ritual" },
  r6:  { titulo: "Sprint fechada com pendências", descricao: "Sprint encerrada mas atividades continuam abertas.",      tipo_default: "decisao", sev_default: "critica", acao: "Decidir destino" },
  r7:  { titulo: "Ata pendente",                descricao: "Ata de reunião não emitida no prazo previsto.",             tipo_default: "aviso",   sev_default: "baixa",   acao: "Criar ata agora" },
  r8:  { titulo: "Consumo de horas",            descricao: "Consumo previsto vs. real fora da faixa esperada.",         tipo_default: "alerta",  sev_default: "media",   acao: "Rever estimativa" },
  r9:  { titulo: "Escopo desatualizado",        descricao: "Documento de escopo sem revisão dentro da cadência.",       tipo_default: "aviso",   sev_default: "media",   acao: "Revisar escopo" },
  r10: { titulo: "Cadência ajustada",           descricao: "Cadência do projeto reduzida por padrão de ignorados.",     tipo_default: "sinal",   sev_default: "baixa",   acao: null },
  r11: { titulo: "N dias antes do início",      descricao: "Aviso configurado por antecedência do início.",             tipo_default: "aviso",   sev_default: "baixa",   acao: "Preparar entrada" },
  r12: { titulo: "N dias antes do fim",         descricao: "Aviso configurado por antecedência do fim.",                tipo_default: "alerta",  sev_default: "alta",    acao: "Validar entrega" },
  r13: { titulo: "Alerta customizado",          descricao: "Regra criada pelo GP nas configurações do projeto.",        tipo_default: "alerta",  sev_default: "media",   acao: "Rever gatilho" },
};

export const REGRAS_COM_CIENTE = new Set(["r2"]);

// ─────────────────────────────────────────────────────────────────────────────
// Projetos-exemplo (offline). Online: substituídos pelos UUIDs reais do backend.
// A referência `PROJETOS_MOCK` é MUTATED após bootstrap online (mesmo array).
// ─────────────────────────────────────────────────────────────────────────────
const PROJETOS_OFFLINE = [
  { id: "p1", codigo: "VEC-2026-01", nome: "Reestruturação Operacional 2026", cor: "sky" },
  { id: "p2", codigo: "VEC-2026-02", nome: "Excelência Comercial B2B",        cor: "violet" },
  { id: "p3", codigo: "NRD-2026-01", nome: "Plano de Governança Corporativa", cor: "amber" },
  { id: "p4", codigo: "SOL-2026-01", nome: "Diagnóstico Estratégico Inicial", cor: "rose" },
];
export const PROJETOS_MOCK = [...PROJETOS_OFFLINE];

let _projPorId = Object.fromEntries(PROJETOS_MOCK.map((p) => [p.id, p]));
function refreshProjIndex() {
  _projPorId = Object.fromEntries(PROJETOS_MOCK.map((p) => [p.id, p]));
}
export function getProjetoDoItem(item) {
  return _projPorId[item.projeto_id] || { id: item.projeto_id, nome: "Projeto?", codigo: "?", cor: "slate" };
}

// ─────────────────────────────────────────────────────────────────────────────
// Detecção de sessão (online vs offline)
// ─────────────────────────────────────────────────────────────────────────────
async function hasAuthSession() {
  try {
    const supa = await getSupabase();
    if (!supa) return false;
    const { data } = await supa.auth.getSession();
    return !!data?.session?.access_token;
  } catch { return false; }
}

async function authHeaders() {
  const token = await getAccessToken().catch(() => null);
  return token ? { Authorization: `Bearer ${token}`, ...impHeaders() } : null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Camada localStorage (fallback offline / demo anônimo) — mantém compat total
// ─────────────────────────────────────────────────────────────────────────────
function iso(daysAgo = 0) { const d = new Date(); d.setDate(d.getDate() - daysAgo); return d.toISOString(); }
function seedOffline() {
  return [
    { id: "s360-p1-1", regra: "r6", projeto_id: "p1", titulo: "Sprint 03 fechada com 4 atividades abertas", tipo: "decisao", sev: "critica", area: "ativos", entrada: iso(0), extra: { atividades_abertas: 4 } },
    { id: "s360-p1-2", regra: "r2", projeto_id: "p1", titulo: "Atraso em 'Migração de dados legado' moveu o caminho crítico em 8 dias", tipo: "decisao", sev: "alta", area: "ativos", entrada: iso(1), extra: { impacto_dias: 8 } },
    { id: "s360-p1-3", regra: "r9", projeto_id: "p1", titulo: "Escopo sem revisão há 62 dias (cadência: 45d)", tipo: "aviso", sev: "media", area: "ativos", entrada: iso(4), extra: { dias: 62, cadencia: 45 } },
    { id: "s360-p1-4", regra: "r4", projeto_id: "p1", titulo: "'Deploy do módulo Financeiro' bloqueado há 4 dias sem update", tipo: "alerta", sev: "alta", area: "ativos", entrada: iso(2), extra: { dias_sem_update: 4 } },
    { id: "s360-p1-5", regra: "r11", projeto_id: "p1", titulo: "Kickoff da fase 2 em 15 dias — checklist a preparar", tipo: "aviso", sev: "baixa", area: "adiados", entrada: iso(3), extra: { adiadoAte: "10/Fev/2026", diasRestantes: 4, motivo: "Aguardando confirmação do sponsor" } },
    { id: "s360-p1-6", regra: "r1", projeto_id: "p1", titulo: "1 atividade sem responsável no Sprint 04", tipo: "aviso", sev: "baixa", area: "ignorados", entrada: iso(6), extra: { ignoradoEm: "28/Jan/2026", motivo: "Será atribuída no próximo planning" } },
    { id: "s360-p1-7", regra: "r2", projeto_id: "p1", titulo: "Alteração no marco 'Go-live Fase 1' movimentou caminho crítico em 3 dias", tipo: "decisao", sev: "alta", area: "cientes", entrada: iso(5), extra: { cienteEm: "30/Jan/2026", cienteEmIso: iso(2) } },
    { id: "s360-p2-1", regra: "r3", projeto_id: "p2", titulo: "Ana Prado alocada em 63h/semana (3 projetos)", tipo: "alerta", sev: "alta", area: "ativos", entrada: iso(1), extra: { horas_semana: 63, capacidade: 40, projetos: 3 } },
    { id: "s360-p2-2", regra: "r5", projeto_id: "p2", titulo: "Retrospectiva Sprint 02 não realizada", tipo: "alerta", sev: "alta", area: "ativos", entrada: iso(0), extra: { ritual: "Retrospectiva" } },
    { id: "s360-p2-3", regra: "r13", projeto_id: "p2", titulo: "Regra custom: 'CAC do trimestre > R$ 850' disparou", tipo: "alerta", sev: "media", area: "ativos", entrada: iso(1), extra: { valor: "R$ 912" } },
    { id: "s360-p2-4", regra: "r2", projeto_id: "p2", titulo: "Dependência entre 'Treinamento SDR' e 'Playbook v2' criou folga negativa", tipo: "decisao", sev: "alta", area: "ativos", entrada: iso(2), extra: { folga: -3 } },
    { id: "s360-p2-5", regra: "r7", projeto_id: "p2", titulo: "Ata do Comitê Comercial de 20/Jan ainda não emitida", tipo: "aviso", sev: "baixa", area: "adiados", entrada: iso(4), extra: { adiadoAte: "12/Fev/2026", diasRestantes: 6, motivo: "Sponsor pediu revisão de conteúdo" } },
    { id: "s360-p2-6", regra: "r8", projeto_id: "p2", titulo: "Consumo de horas em 'Setup CRM' 25% acima do previsto", tipo: "alerta", sev: "media", area: "adiados", entrada: iso(6), extra: { adiadoAte: "15/Fev/2026", diasRestantes: 9, motivo: "Rever no próximo planning" } },
    { id: "s360-p2-7", regra: "r10", projeto_id: "p2", titulo: "Cadência do MGC ajustada (0.8×) — muitos ignorados no último mês", tipo: "sinal", sev: "baixa", area: "ignorados", entrada: iso(5), extra: { ignoradoEm: "26/Jan/2026", motivo: "Já resolvido offline" } },
    { id: "s360-p2-8", regra: "r2", projeto_id: "p2", titulo: "Deslocamento do marco 'Piloto B2B' impactou dependência com Marketing", tipo: "decisao", sev: "alta", area: "cientes", entrada: iso(7), extra: { cienteEm: "27/Jan/2026", cienteEmIso: iso(4) } },
    { id: "s360-p3-1", regra: "r4", projeto_id: "p3", titulo: "'Integração API SAP' bloqueada há 6 dias sem update", tipo: "alerta", sev: "alta", area: "ativos", entrada: iso(2), extra: { dias_sem_update: 6 } },
    { id: "s360-p3-2", regra: "r12", projeto_id: "p3", titulo: "Fim do projeto em 14 dias — 8 atividades em aberto", tipo: "alerta", sev: "alta", area: "ativos", entrada: iso(0), extra: { dias_restantes: 14, atividades_abertas: 8 } },
    { id: "s360-p3-3", regra: "r7", projeto_id: "p3", titulo: "Ata da reunião de 12/Jan ainda não emitida", tipo: "aviso", sev: "baixa", area: "ativos", entrada: iso(3), extra: {} },
    { id: "s360-p3-4", regra: "r9", projeto_id: "p3", titulo: "Documento de escopo sem revisão há 40 dias", tipo: "aviso", sev: "media", area: "adiados", entrada: iso(5), extra: { adiadoAte: "18/Fev/2026", diasRestantes: 12, motivo: "Aguardando aprovação do jurídico" } },
    { id: "s360-p3-5", regra: "r1", projeto_id: "p3", titulo: "2 atividades sem responsável no Sprint 05", tipo: "aviso", sev: "media", area: "ignorados", entrada: iso(4), extra: { ignoradoEm: "29/Jan/2026", motivo: "Serão atribuídas manualmente" } },
    { id: "s360-p3-6", regra: "r2", projeto_id: "p3", titulo: "Reorganização de dependências entre trilhas moveu caminho crítico em 5 dias", tipo: "decisao", sev: "alta", area: "cientes", entrada: iso(6), extra: { cienteEm: "25/Jan/2026", cienteEmIso: iso(3) } },
    { id: "s360-p4-1", regra: "r8", projeto_id: "p4", titulo: "Consumo em 'Análise de mercado' 40% acima do previsto", tipo: "alerta", sev: "media", area: "ativos", entrada: iso(3), extra: { real: "84h", previsto: "60h" } },
    { id: "s360-p4-2", regra: "r1", projeto_id: "p4", titulo: "3 atividades sem responsável no Sprint 02", tipo: "aviso", sev: "media", area: "ativos", entrada: iso(2), extra: { qtd: 3 } },
    { id: "s360-p4-3", regra: "r11", projeto_id: "p4", titulo: "Início da fase 2 em 10 dias — checklist de kickoff", tipo: "aviso", sev: "baixa", area: "ativos", entrada: iso(1), extra: {} },
    { id: "s360-p4-4", regra: "r13", projeto_id: "p4", titulo: "Regra custom: 'NPS < 40 no diagnóstico' disparou", tipo: "alerta", sev: "media", area: "adiados", entrada: iso(4), extra: { adiadoAte: "14/Fev/2026", diasRestantes: 8, motivo: "Aguardando nova pesquisa" } },
    { id: "s360-p4-5", regra: "r10", projeto_id: "p4", titulo: "Cadência ajustada — sinais de baixa engajamento do sponsor", tipo: "sinal", sev: "baixa", area: "ignorados", entrada: iso(7), extra: { ignoradoEm: "24/Jan/2026", motivo: "Sponsor confirmou verbalmente" } },
    { id: "s360-p4-6", regra: "r2", projeto_id: "p4", titulo: "Novo cronograma da fase 3 empurrou marco final em 6 dias", tipo: "decisao", sev: "alta", area: "cientes", entrada: iso(8), extra: { cienteEm: "22/Jan/2026", cienteEmIso: iso(5) } },
  ];
}

function readLocal() {
  const key = LS_KEY();
  try {
    const raw = localStorage.getItem(key);
    if (!raw) {
      // Phase 12.4 — Bucket "mock" (offline/demo) usa o seed histórico.
      // Buckets reais (com workspace_id) começam vazios — nada de dados-persona.
      if (key.endsWith("__mock")) {
        const s = seedOffline();
        localStorage.setItem(key, JSON.stringify(s));
        return s;
      }
      return [];
    }
    return JSON.parse(raw);
  } catch {
    return key.endsWith("__mock") ? seedOffline() : [];
  }
}
function writeLocal(list) {
  try { localStorage.setItem(LS_KEY(), JSON.stringify(list)); window.dispatchEvent(new Event(EVENT)); } catch { /* quota */ }
}

// ─────────────────────────────────────────────────────────────────────────────
// Camada backend (online) — persiste em sprints.projetos + mgc.itens
// ─────────────────────────────────────────────────────────────────────────────
async function bootstrapProjetos(headers) {
  // Phase 12.4 — Não fazemos auto-seed. Tenants novos começam com portfolio
  // vazio e criam projetos manualmente (evita dados-persona fictícios
  // aparecerem no primeiro acesso). Se quiser re-popular exemplos, é preciso
  // chamar POST /api/sprints360/projetos/auto-seed explicitamente.
  const { data } = await axios.get(`${API_BASE}/api/sprints360/projetos`, { headers, timeout: 15000 });
  return data?.projetos || [];
}

async function fetchItensDeTodosProjetos(headers, projetos) {
  const chamadas = projetos.map((p) =>
    axios.get(`${API_BASE}/api/mgc/s360/itens?projeto_id=${p.id}`, { headers, timeout: 15000 })
      .then((r) => (r?.data?.itens || []).map((it) => ({
        // Normalização: mapeamento de campos backend → shape do frontend
        id: it.id,
        regra: it.regra_id || "r13",
        projeto_id: it.projeto_id,
        titulo: it.titulo,
        tipo: it.tipo,
        sev: it.sev,
        area: it.area,
        entrada: it.entrada,
        extra: it.extra || {},
      })))
      .catch(() => []),
  );
  const arrays = await Promise.all(chamadas);
  return arrays.flat();
}

async function moveItemBackend(headers, id, targetArea, extra) {
  const body = { target_area: targetArea };
  if (extra && Object.keys(extra).length) body.extra = { ...extra };
  if (extra?.motivo) body.motivo = extra.motivo;
  const r = await axios.patch(`${API_BASE}/api/mgc/s360/itens/${id}/mover`, body, { headers, timeout: 15000 });
  return r?.data;
}

async function executarMotorBackend(headers, projetoId, forcar = false) {
  try {
    const r = await axios.post(`${API_BASE}/api/mgc/s360/motor/executar`,
      { projeto_id: projetoId, forcar: !!forcar },
      { headers, timeout: 90000 });
    return r?.data;
  } catch (e) {
    console.warn("Motor S360 falhou:", e?.response?.data || e?.message);
    return null;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Cache em memória do modo online (evita rebusca por trocas de projeto)
// ─────────────────────────────────────────────────────────────────────────────
const _cache = {
  online: null,             // true | false | null (indeterminado)
  itens: null,              // array
  projetos: null,           // array
  boot_promise: null,       // Promise em andamento
  headers: null,
};

async function bootstrapOnlineOnce() {
  if (_cache.boot_promise) return _cache.boot_promise;

  _cache.boot_promise = (async () => {
    const online = await hasAuthSession();
    _cache.online = online;
    if (!online) return { projetos: null, itens: null };

    const headers = await authHeaders();
    _cache.headers = headers;
    // Jun/2026 — o 400 "empresa não resolvida" (Super Admin sem workspace)
    // subia como unhandled rejection e estourava o overlay de erro do CRA.
    let projetos = [];
    try {
      projetos = await bootstrapProjetos(headers);
    } catch (e) {
      console.warn("[s360MgcStore] bootstrapProjetos falhou:", e?.response?.status || e?.message);
      _cache.boot_promise = null;   // permite nova tentativa quando o tenant for resolvido
      return { projetos: null, itens: null };
    }
    _cache.projetos = projetos;

    // Substitui PROJETOS_MOCK pelas UUIDs reais (preserva ordem)
    PROJETOS_MOCK.length = 0;
    for (const p of projetos) {
      PROJETOS_MOCK.push({
        id: p.id, codigo: p.codigo, nome: p.nome,
        cor: p.cor || "slate",
      });
    }
    refreshProjIndex();
    try { localStorage.setItem(LS_PROJETOS_KEY(), JSON.stringify(PROJETOS_MOCK)); } catch { /* quota */ }

    const itens = await fetchItensDeTodosProjetos(headers, projetos);
    _cache.itens = itens;
    window.dispatchEvent(new Event(EVENT));
    return { projetos, itens };
  })();

  return _cache.boot_promise;
}

// Restaura projetos online do localStorage antes do fetch (para UI não piscar).
// Phase 12.4 — Só restaura se o bucket é do workspace atual; caso contrário,
// PROJETOS_MOCK começa vazio (evita vazamento entre tenants no mesmo browser).
try {
  const rawP = localStorage.getItem(LS_PROJETOS_KEY());
  if (rawP) {
    const list = JSON.parse(rawP);
    if (Array.isArray(list) && list.length > 0) {
      PROJETOS_MOCK.length = 0;
      list.forEach((p) => PROJETOS_MOCK.push(p));
      refreshProjIndex();
    }
  } else {
    // Sem cache local → limpa o array default (bucket "mock" offline não usa este path).
    if (currentWorkspaceScope() !== "mock") {
      PROJETOS_MOCK.length = 0;
      refreshProjIndex();
    }
  }
} catch { /* ignore */ }

// Ouve troca de sessão para invalidar o cache em memória e forçar rebootstrap.
if (typeof window !== "undefined") {
  window.addEventListener("app-session-changed", () => {
    _cache.online = null;
    _cache.itens = null;
    _cache.projetos = null;
    _cache.boot_promise = null;
    _cache.headers = null;
    // Recarrega PROJETOS_MOCK a partir do novo bucket (ou zera).
    try {
      const rawP = localStorage.getItem(LS_PROJETOS_KEY());
      PROJETOS_MOCK.length = 0;
      if (rawP) {
        const list = JSON.parse(rawP);
        if (Array.isArray(list)) list.forEach((p) => PROJETOS_MOCK.push(p));
      }
      refreshProjIndex();
    } catch { /* ignore */ }
    window.dispatchEvent(new Event(EVENT));
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// API pública (interface preservada)
// ─────────────────────────────────────────────────────────────────────────────
export function useS360MgcItens() {
  const [itens, setItens] = useState(() => (_cache.online === true && _cache.itens) ? _cache.itens : readLocal());
  const [ready, setReady] = useState(_cache.online !== null);

  useEffect(() => {
    let alive = true;
    (async () => {
      const { itens: online } = await bootstrapOnlineOnce();
      if (!alive) return;
      if (_cache.online === true && Array.isArray(online)) {
        setItens(online);
      } else {
        setItens(readLocal());
      }
      setReady(true);
    })();

    const onUpdate = () => {
      if (!alive) return;
      if (_cache.online === true && _cache.itens) setItens([..._cache.itens]);
      else setItens(readLocal());
    };
    window.addEventListener(EVENT, onUpdate);
    return () => { alive = false; window.removeEventListener(EVENT, onUpdate); };
  }, []);

  // Marker: false mientras o bootstrap está em curso; true depois. (Não quebrando compat.)
  itens._ready = ready;
  return itens;
}

export async function moveS360Item(id, targetArea, extra = null) {
  // Online?
  if (_cache.online === true && _cache.itens) {
    const headers = _cache.headers || (await authHeaders());
    try {
      const updated = await moveItemBackend(headers, id, targetArea, extra || {});
      // Merge back no cache
      const idx = _cache.itens.findIndex((i) => i.id === id);
      const novo = {
        id: updated.id,
        regra: updated.regra_id || "r13",
        projeto_id: updated.projeto_id,
        titulo: updated.titulo,
        tipo: updated.tipo,
        sev: updated.sev,
        area: updated.area,
        entrada: updated.entrada,
        extra: updated.extra || {},
      };
      if (idx >= 0) _cache.itens[idx] = novo; else _cache.itens.push(novo);
      // Phase 12.10 — mover item entre áreas (Adiar/Ignorar/Ciente/Reativar…)
      // muda a "leitura" da Val, então o projeto vira dirty.
      if (novo.projeto_id) markProjetoDirty(novo.projeto_id);
      window.dispatchEvent(new Event(EVENT));
      return novo;
    } catch (e) {
      console.error("moveS360Item backend fail", e?.response?.data || e?.message);
      return null;
    }
  }
  // Offline
  const list = readLocal();
  const idx = list.findIndex((it) => it.id === id);
  if (idx === -1) return null;
  const item = { ...list[idx], area: targetArea };
  if (extra) item.extra = { ...(item.extra || {}), ...extra };
  list[idx] = item;
  writeLocal(list);
  if (item.projeto_id) markProjetoDirty(item.projeto_id);
  return item;
}

export function resetS360Seed() {
  if (_cache.online === true) {
    // Online: recarrega do banco (sem apagar dados reais do tenant)
    _cache.boot_promise = null;
    bootstrapOnlineOnce();
    return;
  }
  localStorage.removeItem(LS_KEY);
  window.dispatchEvent(new Event(EVENT));
}

// Getter para o modo (usado por telas para exibir badge "Online/Offline" se quiser)
export function getS360Modo() {
  return { online: _cache.online === true, ready: _cache.online !== null };
}

/**
 * Dispara o motor determinístico R1-R17 para um projeto específico.
 *
 * @param {string} projetoId — UUID do projeto (obrigatório)
 * @param {{ forcar?: boolean }} [opts] — se `forcar=true`, bypassa cooldown do backend
 * @returns {Promise<{executado: boolean, motivo?: string, itens_criados?: number,
 *                     minutos_restantes?: number, cooldown_horas?: number, ...}>}
 */
export async function dispararMotorS360(projetoId, opts = {}) {
  if (!projetoId) return null;
  // Garante que o bootstrap terminou antes de decidir online/offline —
  // sem isso, se essa chamada acontece ANTES de useS360MgcItens iniciar,
  // `_cache.online` ainda é null e o motor era pulado ("offline").
  if (_cache.online === null) {
    try { await bootstrapOnlineOnce(); } catch { /* segue */ }
  }
  if (_cache.online !== true) return { executado: false, motivo: "offline" };
  const headers = _cache.headers || (await authHeaders());
  if (!headers) return { executado: false, motivo: "sem_auth" };
  const forcar = !!opts.forcar;
  const resultado = await executarMotorBackend(headers, projetoId, forcar);
  if (resultado?.executado) {
    // Motor executou (com ou sem itens novos) — marca "motor rodou" para o
    // change-detection e recarrega itens do projeto.
    markProjetoMotorRan(projetoId, resultado.executado_em || null);
    try {
      const r = await axios.get(`${API_BASE}/api/mgc/s360/itens?projeto_id=${projetoId}`, { headers, timeout: 15000 });
      const novos = (r?.data?.itens || []).map((it) => ({
        id: it.id, regra: it.regra_id || "r13",
        projeto_id: it.projeto_id, titulo: it.titulo,
        tipo: it.tipo, sev: it.sev, area: it.area,
        entrada: it.entrada, extra: it.extra || {},
      }));
      // Remove antigos do mesmo projeto e substitui
      if (_cache.itens) {
        _cache.itens = _cache.itens.filter((i) => i.projeto_id !== projetoId).concat(novos);
        window.dispatchEvent(new Event(EVENT));
      }
    } catch (e) { /* mantém cache atual */ }
  }
  return resultado;
}

/**
 * Phase 12.5 — "Atualizar informações" da tela Ações e Decisões.
 *
 * Refaz o mesmo fluxo do primeiro acesso à tela:
 *   1. Invalida o cache em memória (`_cache`) para forçar re-fetch.
 *   2. Reexecuta `bootstrapOnlineOnce` — busca lista de projetos + itens
 *      atuais do backend.
 *   3. Se `projetoId` for informado, dispara o motor MGC pra esse projeto
 *      (com `forcar=true` por padrão — o botão manual bypassa cooldown).
 *   4. Dispara o evento `EVENT` para consumidores (`useS360MgcItens`)
 *      re-renderizarem.
 *
 * @param {string|null} projetoId
 * @param {{ forcar?: boolean }} [opts] — default `forcar=true` (uso via botão)
 * @returns {Promise<{ok: boolean, motorResult?: object}>}
 */
export async function refreshS360Mgc(projetoId = null, opts = {}) {
  // 1. Invalida cache — força bootstrap a rodar de novo
  _cache.online = null;
  _cache.itens = null;
  _cache.projetos = null;
  _cache.boot_promise = null;

  // 2. Bootstrap (lista projetos + itens)
  const { itens } = await bootstrapOnlineOnce();

  // 3. Motor MGC para o projeto atual — força por padrão (contrato do botão
  // "Atualizar Informações"; auto-run inicial passa forcar=true também)
  let motorResult = null;
  if (projetoId) {
    const forcar = opts.forcar !== false; // default true
    motorResult = await dispararMotorS360(projetoId, { forcar });
  }

  // 4. Notifica consumidores
  window.dispatchEvent(new Event(EVENT));
  return { ok: Array.isArray(itens) || _cache.online !== true, motorResult };
}

/**
 * Lista os itens do projeto que passaram pelo Motor MGC e foram fechados
 * (area='resolvido' pela RULE 4 · CLOSURE). Ordenados por data de resolução
 * DESC — mais recentes primeiro. Trace: cada linha inclui atividade_id +
 * regra_id (alert_type) para auditoria direta via SQL.
 *
 * Modo offline: retorna [] (o fallback local não simula closure).
 *
 * @param {string} projetoId — UUID do projeto
 * @returns {Promise<Array<{
 *   id: string, titulo: string, regra_id: string,
 *   atividade_id: string|null, tipo: string, sev: string,
 *   entrada: string, resolvido_em: string, extra: object|null
 * }>>}
 */
export async function fetchResolvidosProjeto(projetoId) {
  if (!projetoId) return [];
  if (_cache.online === null) {
    try { await bootstrapOnlineOnce(); } catch { /* segue */ }
  }
  if (_cache.online !== true) return [];
  const headers = _cache.headers || (await authHeaders());
  if (!headers) return [];
  try {
    const r = await axios.get(
      `${API_BASE}/api/mgc/s360/itens?projeto_id=${projetoId}&area=resolvido`,
      { headers, timeout: 15000 },
    );
    return (r?.data?.itens || []).map((it) => ({
      id: it.id,
      titulo: it.titulo,
      regra_id: it.regra_id || "r13",
      atividade_id: it.atividade_id || null,
      tipo: it.tipo,
      sev: it.sev,
      entrada: it.entrada,
      resolvido_em: it.resolvido_em,
      extra: it.extra || null,
    }));
  } catch (e) {
    console.warn("fetchResolvidosProjeto:", e?.response?.data || e?.message);
    return [];
  }
}
